<template>
  <div class="data-container">
    <!-- 顶部用户信息栏 -->
    <div class="user-info-bar">
      <div class="user-info">
        <el-avatar :size="40" icon="User" class="user-avatar"></el-avatar>
        <span class="username">{{ currentUsername || '未知用户' }}</span>
      </div>
      <el-button type="text" @click="handleLogout" class="logout-btn hover-effect">
        <el-icon class="logout-icon"><SignOut /></el-icon>
        退出登录
      </el-button>
    </div>

    <el-page-header content="数据展示" class="page-header"></el-page-header>
    
    <el-card shadow="hover" class="filter-card custom-card">
      <el-row :gutter="24" class="filter-row">
        <!-- 下属筛选下拉框 -->
        <el-col :span="8">
          <el-form-item label="下属筛选" class="form-item">
            <el-select v-model="selectedStaffId" placeholder="请选择下属" @change="fetchData" class="custom-select">
              <el-option label="全部下属" value="all"></el-option>
              <el-option v-for="staff in staffList" :key="staff.id" :label="staff.name" :value="staff.id"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <!-- 地区筛选下拉框 -->
        <el-col :span="8">
          <el-form-item label="地区筛选" class="form-item">
            <el-select v-model="selectedArea" placeholder="请选择地区" @change="fetchData" class="custom-select">
              <el-option label="全部地区" value="all"></el-option>
              <el-option v-for="area in areas" :key="area.value" :label="area.label" :value="area.value"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <!-- 时间范围选择器（替换原来的下拉框） -->
        <el-col :span="8">
          <el-form-item label="时间范围" class="form-item">
            <el-date-picker
              v-model="selectedDateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              @change="fetchData"
              class="custom-select"
              value-format="YYYY-MM-DD"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-card>

    <el-card shadow="hover" class="data-table-card custom-card">
      <!-- 未选择下属提示 -->
      <div v-if="selectedStaffId === 'all'" class="empty-tip">
        <el-empty description="请选择具体下属以查看数据" class="empty-component"></el-empty>
      </div>
      
      <!-- 数据表格（添加数据加载中状态） -->
      <el-table v-else :data="tableData" border stripe class="custom-table" :header-cell-style="headerCellStyle" v-loading="isLoading">
        <el-table-column label="ID" prop="id" align="center" width="80"></el-table-column>
        <el-table-column label="地区" prop="area" align="center" width="120">
          <template #default="scope">
            <el-tag type="info" effect="plain" class="area-tag">{{ getAreaLabel(scope.row.area) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="用户名" prop="username" align="center" width="140"></el-table-column>
        <el-table-column label="数值" prop="value" align="center" width="100">
          <template #default="scope">
            <span class="value-text">{{ scope.row.value }}</span>
          </template>
        </el-table-column>
        <el-table-column label="时间" prop="time" align="center"></el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { ElAvatar, ElEmpty, ElMessage, ElButton, ElIcon, ElPageHeader, ElCard, ElRow, ElCol, ElFormItem, ElSelect, ElOption, ElTable, ElTableColumn, ElTag, ElDatePicker } from 'element-plus'
import { SignOut } from '@element-plus/icons-vue'

const router = useRouter()

// 筛选条件
const selectedArea = ref('all')
const selectedStaffId = ref('all')
const selectedStaffName = ref('')

// 日期范围（新）
const selectedDateRange = ref([]) // [开始日期, 结束日期]

const areas = ref([])
const staffList = ref([])
const tableData = ref([])
const currentUsername = ref('')
const isLoading = ref(false)

// 表格头部样式
const headerCellStyle = computed(() => {
  return {
    'background-color': '#f0f7ff',
    'color': '#2c3e50',
    'font-weight': '600',
    'font-size': '14px'
  }
})

// 退出登录
const handleLogout = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('savedUser')
  router.push('/login')
}

// 地区映射
const getAreaLabel = (value) => {
  if (value === 'all') return '全部地区'
  const targetArea = areas.value.find(item => item.value === value)
  return targetArea ? targetArea.label : '未知地区'
}

// 获取地区列表
const fetchAreas = async () => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      router.push('/login')
      return
    }
    const res = await axios.get(`https://log.wuxiahaojie.com/api/Area`, {  
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (res.data.code === 200) {
      areas.value = res.data.data || []
    } else {
      ElMessage.error(`获取地区列表失败：${res.data.msg}`)
    }
  } catch (err) {
    console.error('获取地区列表异常：', err)
    ElMessage.error('获取地区数据失败')
  }
}

// 获取下属列表
const fetchStaffList = async () => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      router.push('/login')
      return
    }
    const userInfoStr = localStorage.getItem('userInfo')
    if (!userInfoStr) {
      ElMessage.error('请重新登录')
      router.push('/login')
      return
    }
    const userInfo = JSON.parse(userInfoStr)
    currentUsername.value = userInfo.username || ''

    const res = await axios.get(`https://log.wuxiahaojie.com/api/Admin/staffs?username=${currentUsername.value}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (res.data.code === 200) {
      staffList.value = res.data.data || []
      if (staffList.value.length === 0) {
        ElMessage.info('当前用户暂无下属数据')
      }
    } else {
      ElMessage.error(`获取下属失败：${res.data.msg || '未知错误'}`)
    }
  } catch (err) {
    console.error('获取下属异常：', err)
    ElMessage.error('获取下属数据失败')
  }
}

// 获取数据（已修改为 startTime + endTime）
const fetchData = async () => {
  if (selectedStaffId.value === 'all') {
    tableData.value = []
    selectedStaffName.value = ''
    return
  }

  // 没选时间范围也不请求
  if (!selectedDateRange.value || selectedDateRange.value.length !== 2) {
    ElMessage.warning('请选择时间范围')
    tableData.value = []
    return
  }

  const selectedStaff = staffList.value.find(staff => staff.id === selectedStaffId.value)
  if (!selectedStaff) {
    ElMessage.error('未找到下属信息')
    tableData.value = []
    return
  }
  selectedStaffName.value = selectedStaff.name

  isLoading.value = true
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      router.push('/login')
      return
    }

    const params = new URLSearchParams()
    params.append('area', selectedArea.value)
    // 关键：替换成 startTime 和 endTime
    params.append('timeRange', selectedDateRange.value[0])
    params.append('endTime', selectedDateRange.value[1])
    params.append('staffName', selectedStaffName.value)

    console.log('最终请求参数：', Object.fromEntries(params))

    const res = await axios.get(`https://log.wuxiahaojie.com/api/Data/getuserdata`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      params: params
    })

    if (res.data.Code === 200 || res.data.code === 200) {
      tableData.value = res.data.Data || res.data.data || []
      if (tableData.value.length === 0) {
        ElMessage.info(`当前筛选下暂无数据`)
      }
    } else {
      const msg = res.data.Msg || res.data.msg || '获取失败'
      ElMessage.error(msg)
      tableData.value = []
    }
  } catch (err) {
    console.error('数据接口异常：', err)
    ElMessage.error('数据加载失败，请稍后重试')
    tableData.value = []
  } finally {
    isLoading.value = false
  }
}

// 初始化
onMounted(async () => {
  const token = localStorage.getItem('token')
  if (!token) {
    router.push('/login')
    return
  }
  await fetchAreas()
  await fetchStaffList()
})
</script>

<style scoped>
/* 全局容器样式 */
.data-container {
  padding: 1.5rem;
  background-color: #f9fafc;
  min-height: 100vh;
}

/* 用户信息栏样式 */
.user-info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.user-info-bar:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.user-avatar {
  background-color: #409eff;
  color: #ffffff;
  font-size: 20px;
}

.username {
  font-size: 16px;
  color: #2d3748;
  font-weight: 600;
}

.logout-btn {
  color: #409eff;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.3s ease;
}

.logout-btn:hover {
  color: #3a8ee6;
  transform: translateY(-2px);
}

.logout-icon {
  font-size: 16px;
}

/* 页面标题样式 */
.page-header {
  margin-bottom: 1.5rem;
  --el-page-header-content-font-size: 22px;
  --el-page-header-content-color: #2d3748;
  --el-page-header-padding: 0;
}

/* 自定义卡片样式 */
.custom-card {
  border-radius: 16px;
  border: none;
  overflow: hidden;
  transition: all 0.3s ease;
}

.custom-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.filter-card {
  margin-bottom: 1.5rem;
  padding: 1.5rem;
}

.data-table-card {
  padding: 1.5rem;
}

/* 筛选行和表单项样式 */
.filter-row {
  align-items: center;
}

.form-item {
  margin-bottom: 0;
  --el-form-item-label-font-size: 14px;
  --el-form-item-label-color: #606266;
  --el-form-item-margin-bottom: 0;
}

.custom-select {
  width: 100%;
  --el-select-input-font-size: 14px;
  --el-select-dropdown-font-size: 14px;
  border-radius: 8px;
  --el-select-hover-border-color: #409eff;
}

/* 空数据提示样式 */
.empty-tip {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

.empty-component {
  --el-empty-description-font-size: 16px;
  --el-empty-description-color: #909399;
}

/* 自定义表格样式 */
.custom-table {
  --el-table-row-hover-bg-color: #f5f7fa;
  --el-table-border-color: #ebeef5;
  font-size: 14px;
  border-radius: 8px;
  overflow: hidden;
}

/* 表格内容样式 */
.area-tag {
  margin: 0;
  font-size: 12px;
}

.value-text {
  font-size: 14px;
  color: #2d3748;
  font-weight: 500;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .el-col {
    --el-col-span: 12 !important;
  }
}

@media (max-width: 768px) {
  .el-col {
    --el-col-span: 24 !important;
  }

  .data-container {
    padding: 1rem;
  }

  .user-info-bar {
    padding: 0.8rem 1rem;
  }

  .username {
    font-size: 14px;
  }
}
</style>